import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { Logo } from "@/components/layout/Logo";
import { PrimaryButton, InputField, TextLink } from "@/components/fionova/Primitives";
import { useAuth } from "@/context/AuthContext";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { resetPassword } = useAuth();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { if (!token) setError("This reset link is invalid or has expired. Please request a new one."); }, [token]);

  const pwValid = form.password.length >= 6;
  const match = form.password === form.confirm;
  const canSubmit = !!token && pwValid && match && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      await resetPassword(token, form.password);
      setDone(true);
    } catch (e) {
      setError(e.message || "Could not reset your password.");
      setBusy(false);
    }
  };

  return (
    <OnboardingLayout testId="page-reset" onBack={() => navigate("/login")}>
      <div className="flex flex-col items-center pt-2 text-center">
        <Logo size={72} />
        <h1 className="mt-8 text-3xl font-semibold tracking-tight text-foreground">
          Choose a new password
        </h1>
        {!done && (
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Pick a password you'll remember. It needs at least 6 characters.
          </p>
        )}
      </div>

      {done ? (
        <div className="mt-10 text-center" data-testid="reset-done">
          <p className="text-base leading-relaxed text-foreground">
            Your password has been updated. You can now log in with your new password.
          </p>
          <div className="mt-8">
            <PrimaryButton data-testid="reset-to-login" onClick={() => navigate("/login")}>
              Go to log in
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <div className="mt-10 flex flex-col gap-4"
          onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") { e.preventDefault(); if (canSubmit) submit(); } }}>
          <div>
            <InputField
              label="New password" testId="reset-password" type="password" autoComplete="new-password"
              placeholder="At least 6 characters"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              disabled={!token}
            />
            {form.password && !pwValid && (
              <p className="mt-1.5 text-xs text-amber-700" data-testid="reset-password-hint">
                Must be at least 6 characters ({form.password.length}/6).
              </p>
            )}
          </div>
          <div>
            <InputField
              label="Confirm new password" testId="reset-confirm" type="password" autoComplete="new-password"
              placeholder="Re-enter your new password"
              value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              disabled={!token}
            />
            {form.confirm && !match && (
              <p className="mt-1.5 text-xs text-amber-700" data-testid="reset-match-hint">
                Passwords don't match yet.
              </p>
            )}
          </div>

          {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}

          <PrimaryButton data-testid="reset-submit" onClick={() => canSubmit && submit()} disabled={!canSubmit} className="mt-2">
            {busy ? "Saving…" : "Reset password"}
          </PrimaryButton>

          {error && (
            <div className="mt-1 text-center">
              <TextLink data-testid="reset-request-new" onClick={() => navigate("/forgot-password")}>
                Request a new reset link
              </TextLink>
            </div>
          )}
        </div>
      )}
    </OnboardingLayout>
  );
}
